import { LocalOverrideAdapter } from "@statsig/js-local-overrides";
import {
  StatsigClient,
  SDKType,
  StatsigMetadataProvider,
} from "@statsig/js-client";
import { runStatsigAutoCapture } from "@statsig/web-analytics";
import packageJson from "../package.json";

const STABLE_ID_ATTRIBUTE = "data-statsig-element-id";
const SIDECAR_SDK_TYPE = "sidecar-v2-beta";
const SIDECAR_SDK_VERSION = `${packageJson.version}`;
const LOCAL_OVERRIDE_BLOCKED_DIRECTIVE_TYPES = new Set([
  "code",
  "content",
  "inject-style",
  "redirect",
]);

const sharedStableIdMap = {};
const existingSidecar = window["StatsigSidecar"] || {};
window["StatsigSidecar"] = Object.assign(existingSidecar, {
  _statsigInstance: null,
  _queuedEvents: [],
  _clientInitialized: false,
  _redirKey: "_stsgnoredir",
  _stableIdMap: sharedStableIdMap,
  _appliedInserts: new Set(),
  _locallyOverriddenExperimentNames: new Set(),

  _sanitizeLocalOverrideExperimentValue: function (value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return {};
    }
    const sanitized = { ...value };
    delete sanitized.sidecarPrerunScript;
    const directives = sanitized["!statsig_sidecar_directives"];
    if (Array.isArray(directives)) {
      sanitized["!statsig_sidecar_directives"] = directives.filter(
        (directive) =>
          directive &&
          typeof directive === "object" &&
          !LOCAL_OVERRIDE_BLOCKED_DIRECTIVE_TYPES.has(directive.type)
      );
    }
    return sanitized;
  },

  activateExperiment: function (expName) {
    // expName is what's shown in console; exp.name is not.
    const matchedExps = this._getMatchingExperiments();
    const foundExp = matchedExps.find(
      (exp) => exp.value["sidecarExperimentName"] === expName
    );
    if (foundExp) {
      StatsigSidecar._performExperiments([foundExp.name]);
    }
  },

  findElementToObserve: function (query) {
    while (query) {
      try {
        const element = this.resolveElement(query);
        if (element) {
          return element;
        }
      } catch (e) { }
      query = query.substring(0, query.lastIndexOf(">"));
    }
    return document.body;
  },

  applyStableIdMap: function () {
    const map = sharedStableIdMap || {};
    Object.keys(map).forEach((selector) => {
      const id = map[selector];
      if (!id) {
        return;
      }
      const element = document.querySelector(selector);
      if (element && !element.hasAttribute(STABLE_ID_ATTRIBUTE)) {
        element.setAttribute(STABLE_ID_ATTRIBUTE, id);
      }
    });
  },

  resolveElement: function (selector) {
    const directElement = document.querySelector(selector);
    if (directElement) {
      const id = this._ensureStableId(directElement);
      sharedStableIdMap[selector] = id;
    }
    const stableId = sharedStableIdMap?.[selector];
    if (stableId) {
      const stableElement = document.querySelector(
        `[${STABLE_ID_ATTRIBUTE}="${stableId}"]`
      );
      if (stableElement) {
        return stableElement;
      }
    }
    return directElement;
  },

  _mergeStableIdMap: function (stableIdMap) {
    if (!stableIdMap || typeof stableIdMap !== "object") {
      return;
    }
    Object.assign(sharedStableIdMap, stableIdMap);
  },

  _constructQueryString: function (element) {
    if (element.id) {
      return `[id="${element.id}"]`;
    }
    const tagName = element.tagName.toLowerCase();
    const parent = element.parentElement;
    if (!parent || tagName === "body") {
      return "body";
    }
    const index = Array.from(parent.children).findIndex(
      (child) => child === element
    );
    return `${this._constructQueryString(parent)} > ${tagName}:nth-child(${index + 1})`;
  },

  _ensureStableId: function (element) {
    let id = element.getAttribute(STABLE_ID_ATTRIBUTE);
    if (!id) {
      id = `statsig-element-${Math.random().toString(36).slice(2, 10)}`;
      element.setAttribute(STABLE_ID_ATTRIBUTE, id);
    }
    return id;
  },

  _updateStableIdMapForElements: function (elements) {
    elements.forEach((element) => {
      if (!element || element.nodeType !== 1) {
        return;
      }
      const id = this._ensureStableId(element);
      const selector = this._constructQueryString(element);
      sharedStableIdMap[selector] = id;
    });
  },

  _flushQueuedEvents: function () {
    if (this._queuedEvents.length === 0) {
      return;
    }
    if (!this._statsigInstance) {
      return;
    }

    const events = [...this._queuedEvents];
    this._queuedEvents = [];
    events.forEach((event) => {
      this._statsigInstance.logEvent(
        event.eventName,
        event.value,
        event.metadata
      );
    });
    this._statsigInstance.flush && this._statsigInstance.flush();
  },

  _getSidecarConfig: function () {
    const config = window?.statsigSidecarConfig;
    return config && typeof config === "object" ? config : {};
  },

  _emitExperimentEvaluation: function (experiment) {
    if (!experiment) {
      return;
    }
    const callback = this._getSidecarConfig().onExperimentEvaluation;
    if (typeof callback !== "function") {
      return;
    }
    try {
      const experimentName =
        experiment?.value?.["sidecarExperimentName"] ?? experiment.name;
      const groupName = experiment?.groupName ?? null;
      callback({
        name: "experiment_evaluation",
        experiment,
        experimentName,
        groupName,
      });
    } catch (e) {
      console.error("Failed to run onExperimentEvaluation callback:", e);
    }
  },

  _getMatchingExperiments: function () {
    const matchingExps = [];
    const configList = this._statsigInstance.getConfigList();
    let url = window.location.href;
    try {
      const u = new URL(url);
      // This check is important or else it messes up the original URL
      if (u.searchParams.has("overrideuser")) {
        u.searchParams.delete("overrideuser");
      }
      url = u.toString();
    } catch (e) { }
    configList.forEach((config) => {
      const exp = this._statsigInstance.getExperiment(config, {
        disableExposureLog: true,
      });

      if (exp.value["!statsig_sidecar_directives"]) {
        if (this._isMatchingExperiment(url, exp.value["sidecarURLFilters"])) {
          matchingExps.push(exp);
        }
      }
    });
    return matchingExps;
  },

  getStatsigInstance: function () {
    return this._statsigInstance;
  },

  _isMatchingExperiment: function (url, filters) {
    if (!filters || filters.length === 0) {
      return true;
    }
    for (const filter of filters) {
      if (filter.type === "contains") {
        if (url.includes(filter.value)) {
          return true;
        }
      } else if (filter.type === "equals") {
        if (url === filter.value) {
          return true;
        }
      } else if (filter.type === "regex") {
        if (RegExp(filter.value).test(url)) {
          return true;
        }
      } else if (filter.type === "path") {
        const path = new URL(url).pathname;
        if (path === filter.value) {
          return true;
        }
      }
    }
    return false;
  },

  _isIOS: function () {
    return /iPad|iPhone|iPod/.test(navigator?.userAgent ?? "");
  },

  logEvent: function (eventName, value, metadata) {
    if (!this._statsigInstance || !this._clientInitialized) {
      this._queuedEvents.push({ eventName, value, metadata });
      return;
    }

    this._flushQueuedEvents();
    this._statsigInstance.logEvent(eventName, value, metadata);
  },

  _observeMutation: function (element, modifierFunc) {
    const config = {
      attributes: true,
      childList: true,
      characterData: true,
      subtree: true,
      characterDataOldValue: false,
    };
    const callback = (mutationsList, observer) => {
      setTimeout(() => {
        modifierFunc();
      }, 0);
    };
    const observer = new MutationObserver(callback);
    observer.observe(element, config);
    modifierFunc();
  },

  _observePageChanges: function (expIds) {
    const ps = history.pushState;
    const rs = history.replaceState;

    const rerun = () => {
      StatsigSidecar.runAllExperiments(expIds);
    };

    history.pushState = function (...n) {
      ps.apply(history, n);
      rerun();
    };
    history.replaceState = function (...n) {
      rs.apply(history, n);
      rerun();
    };
    window.addEventListener("hashchange", rerun);
    window.addEventListener("popstate", rerun);
  },

  _performAfterLoad: function (callback) {
    if (/complete|interactive|loaded/.test(document.readyState)) {
      callback();
    } else {
      document.addEventListener("DOMContentLoaded", () => {
        callback();
      });
    }
  },

  performAttributeChange: function (query, attribute, value, removeAttrs = []) {
    if (!query) {
      return;
    }
    this.setupMutationObserver(query, () => {
      const element = this.resolveElement(query);
      if (!element) {
        return;
      }
      if (element && element.getAttribute(attribute) !== value) {
        element.setAttribute(attribute, value);
        removeAttrs?.forEach((attr) => {
          element.removeAttribute(attr);
        });
      }
    });
  },

  performContentChange: function (query, value) {
    if (!query) {
      return;
    }
    this.setupMutationObserver(query, () => {
      const element = this.resolveElement(query);
      if (!element) {
        return;
      }
      if (element && element.innerHTML !== value) {
        element.innerHTML = value;
      }
    });
  },

  _performDirective: function (directive, expId) {
    switch (directive.type) {
      case "content":
        this._performAfterLoad(() => {
          this.performContentChange(directive.selector, directive.value);
        });
        break;

      case "style":
        this._performAfterLoad(() => {
          this.performStyleChange(
            directive.selector,
            directive.key,
            directive.value
          );
        });
        break;

      case "properties":
        this._performAfterLoad(() => {
          directive.metadata?.forEach((property) => {
            if (property.type === "style") {
              this.performStyleChange(
                directive.selector,
                property.name,
                property.value
              );
            } else if (property.type === "attribute") {
              this.performAttributeChange(
                directive.selector,
                property.name,
                property.value
              );
            }
          });
        });
        break;

      case "attribute":
        this._performAfterLoad(() => {
          this.performAttributeChange(
            directive.selector,
            directive.key,
            directive.value
          );
        });
        break;

      case "image":
        this._performAfterLoad(() => {
          this.performAttributeChange(
            directive.selector,
            "src",
            directive.value,
            ["srcset"]
          );
        });
        break;

      case "reorder":
        this._performAfterLoad(() => {
          this.performReorderElement(
            directive.selector,
            directive.operator,
            directive.value,
            directive.metadata?.after
          );
        });
        break;

      case "insert":
        this._performAfterLoad(() => {
          this.performInsertElement(
            directive.selector,
            directive.value,
            directive.metadata?.position
          );
        });
        break;

      case "code":
        this.performInjectScript(directive.value);
        break;

      case "inject-style":
        this.performInjectStyle(directive.value);
        break;

      case "redirect":
        this.redirectPage(directive.value, expId);
        break;
    }
  },

  _performExperiments: function (expIds) {
    if (Array.isArray(expIds)) {
      this._appliedInserts = new Set();
      expIds.forEach((expId) => {
        const expConfig = this._statsigInstance.getExperiment(expId);
        this._emitExperimentEvaluation(expConfig);
        let directives = expConfig.get("!statsig_sidecar_directives", []);
        if (this._locallyOverriddenExperimentNames.has(expId)) {
          directives = directives.filter(
            (directive) =>
              !LOCAL_OVERRIDE_BLOCKED_DIRECTIVE_TYPES.has(directive?.type)
          );
        }
        const stableIdMap = expConfig.get("!statsig_sidecar_stable_id_map", {});
        this._mergeStableIdMap(stableIdMap);
        directives.forEach((directive) => {
          this._mergeStableIdMap(directive?.metadata?.stableIdMap);
          this._mergeStableIdMap(directive?.stableIdMap);
        });
        this.applyStableIdMap();
        directives.forEach((directive) => {
          try {
            this._performDirective(directive, expId);
          } catch (e) {
            console.error("Failed to perform directive:", e);
          }
        });
      });
    }
  },

  performInjectScript: function (value) {
    const script = document.createElement("script");
    script.setAttribute("nonce", this.scriptNonce);
    script.nonce = this.scriptNonce;
    script.innerHTML = value;
    document.head.appendChild(script);
  },

  performInjectStyle: function (value) {
    const style = document.createElement("style");
    style.innerHTML = value;
    document.head.appendChild(style);
  },

  performReorderElement: function (query, operator, anchorQuery, after) {
    if (!query) {
      return;
    }
    this.setupMutationObserver(query, () => {
      const target = this.resolveElement(query);
      const anchor = this.resolveElement(anchorQuery);
      if (!target || !anchor) {
        return;
      }

      const targetParent = target.parentElement;
      const anchorParent =
        operator === "first" || operator === "last"
          ? anchor
          : anchor.parentElement;
      let reorderAffected = null;
      if (targetParent && anchorParent) {
        if (targetParent === anchorParent) {
          const siblings = Array.from(anchorParent.children);
          const currentIndex = siblings.indexOf(target);
          const anchorIndex = siblings.indexOf(anchor);
          if (currentIndex !== -1 && anchorIndex !== -1) {
            const start = Math.min(currentIndex, anchorIndex);
            const end = Math.max(currentIndex, anchorIndex);
            reorderAffected = siblings.slice(start, end + 1);
            this._updateStableIdMapForElements(reorderAffected);
          }
        } else {
          reorderAffected = [
            ...Array.from(targetParent.children),
            ...Array.from(anchorParent.children),
          ];
          this._updateStableIdMapForElements(reorderAffected);
        }
      }

      if (after !== undefined) {
        if (after) {
          anchor.insertAdjacentElement("afterend", target);
        } else {
          anchor.insertAdjacentElement("beforebegin", target);
        }
        if (reorderAffected) {
          this._updateStableIdMapForElements(reorderAffected);
        }
        return;
      }

      switch (operator) {
        case "before":
          anchor.before(target);
          break;
        case "after":
          anchor.after(target);
          break;
        case "first":
          anchor.prepend(target);
          break;
        case "last":
          anchor.append(target);
          break;
        default:
          break;
      }
      if (reorderAffected) {
        this._updateStableIdMapForElements(reorderAffected);
      }
    });
  },

  performInsertElement: function (query, tagName, position) {
    if (!query || !tagName || !position) {
      return;
    }
    const insertKey = `${query}|${tagName}|${position}`;
    this.setupMutationObserver(query, () => {
      if (this._appliedInserts.has(insertKey)) {
        return;
      }
      const anchor = this.resolveElement(query);
      if (!anchor) {
        return;
      }

      let insertAffected = null;
      if (position === "beforebegin" || position === "afterend") {
        const parent = anchor.parentElement;
        if (parent) {
          const siblings = Array.from(parent.children);
          const anchorIndex = siblings.indexOf(anchor);
          const startIndex =
            position === "beforebegin" ? anchorIndex : anchorIndex + 1;
          if (anchorIndex !== -1) {
            insertAffected = siblings.slice(startIndex);
            this._updateStableIdMapForElements(insertAffected);
          }
        }
      } else {
        const children = Array.from(anchor.children);
        const startIndex = position === "afterbegin" ? 0 : children.length;
        insertAffected = children.slice(startIndex);
        this._updateStableIdMapForElements(insertAffected);
      }

      const inserted = document.createElement(tagName);
      anchor.insertAdjacentElement(position, inserted);
      if (insertAffected) {
        this._updateStableIdMapForElements(insertAffected);
      }
      this._updateStableIdMapForElements([inserted]);
      this._appliedInserts.add(insertKey);
    });
  },

  performStyleChange: function (query, style, value) {
    if (!query) {
      return;
    }
    this.setupMutationObserver(query, () => {
      const element = this.resolveElement(query);
      if (!element) {
        return;
      }
      element.style.setProperty(style, value);
      // const existingStyle = element.getAttribute("style") || "";
      // if (!existingStyle.includes(` ${value}`)) {
      //   const newStyle = `${existingStyle}; ${value}`;
      //   element.setAttribute("style", newStyle);
      // }
    });
  },

  processEvent: function (event) {
    if (!event || !event.detail) {
      return false;
    }

    const detail = event.detail;
    if (detail.name === "inject-script") {
      this.performInjectScript(detail.value);
      return false;
    }
  },

  redirectPage: async function (url, expId) {
    this._flushQueuedEvents();

    if (!window || !window.location || !url || window.location.href == url) {
      return;
    }

    try {
      const currentUrl = new URL(window.location.href);
      if (currentUrl.searchParams.has(this._redirKey)) {
        return;
      }

      const newUrl = new URL(url, currentUrl);
      for (const key of currentUrl.searchParams.keys()) {
        if (!newUrl.searchParams.has(key)) {
          newUrl.searchParams.set(key, currentUrl.searchParams.get(key));
        }
      }

      newUrl.searchParams.set(this._redirKey, "1");
      if (expId) {
        try {
          window.sessionStorage?.setItem(this._redirKey, expId);
        } catch (e) { }
      }

      if (this._isIOS()) {
        await this._statsigInstance.flush();
      }

      window.location.href = newUrl.toString();
    } catch (e) {
      window.location.href = url;
    }
  },

  resetBody: function () {
    const sbpd = document.getElementById("__sbpd");
    if (sbpd) {
      sbpd.parentElement.removeChild(sbpd);
    }
  },

  _getRedirectInitiatedExpIds: function () {
    try {
      const value = window.sessionStorage?.getItem(this._redirKey);
      if (!value) {
        return [];
      }
      window.sessionStorage.removeItem(this._redirKey);
      return value.split(",").filter(Boolean);
    } catch (e) {
      return [];
    }
  },

  runAllExperiments: function (expIds = null) {
    if (!expIds) {
      const matchingExps = this._getMatchingExperiments();
      this._runPreExperimentScripts(matchingExps);
      expIds = matchingExps
        .filter(
          (exp) => !exp.value["sidecarDisableAutoRun"]
        )
        .map((exp) => exp.name);
    }

    const exposureOnlyIds = this._getRedirectInitiatedExpIds().filter(
      (id) => !expIds.includes(id)
    );

    try {
      this._performExperiments(expIds);
    } catch (e) {
      console.error("Failed to perform experiments:", e);
    }

    exposureOnlyIds.forEach((id) => {
      try {
        this._emitExperimentEvaluation(this._statsigInstance.getExperiment(id));
      } catch (e) {
        console.error("Failed to log redirect resolving exposure:", e);
      }
    });

    this.resetBody();
    if (window?.postExperimentCallback) {
      window.postExperimentCallback(this._statsigInstance, [
        ...expIds,
        ...exposureOnlyIds,
      ]);
    }
  },

  _runPreExperimentScripts: function (matchedExps) {
    matchedExps?.forEach((exp) => {
      if (this._locallyOverriddenExperimentNames.has(exp.name)) {
        return;
      }
      if (exp.value["sidecarPrerunScript"]) {
        this.performInjectScript(exp.value["sidecarPrerunScript"]);
      }
    });
  },

  setupMutationObserver: function (query, callback) {
    const observeElement = this.findElementToObserve(query);
    if (observeElement) {
      this._observeMutation(observeElement, callback);
    }
  },

  setupStatsigSdk: async function (
    apiKey,
    expIds,
    autoStart,
    initUrlOverride,
    logUrlOverride
  ) {
    let overrideUserID = null;
    let overrideExp = null;
    let overrideExpName = null;
    try {
      const url = new URL(window.location.href);
      overrideUserID = url.searchParams.get("overrideuser");
      overrideExp = url.searchParams.get("overrideexp");
      overrideExpName = url.searchParams.get("overrideexpname");
    } catch (e) {
      console.error("Failed to update user:", e);
    }

    try {
      const user = window?.statsigUser ?? {};
      if (overrideUserID) {
        user.userID = overrideUserID;
        user.customIDs = user.customIDs || {};
        user.customIDs.stableID = overrideUserID;
      }

      const options = window?.statsigOptions ?? {};
      options.disableLogging = !autoStart;
      const existingNetworkOverride = options?.networkConfig?.networkOverrideFunc;
      const rewriteStatsigSdkParams = (requestUrl) => {
        try {
          const parsedUrl = new URL(requestUrl);
          parsedUrl.searchParams.set("st", SIDECAR_SDK_TYPE);
          parsedUrl.searchParams.set("sv", SIDECAR_SDK_VERSION);
          return parsedUrl.toString();
        } catch (e) {
          return requestUrl;
        }
      };
      if (initUrlOverride || logUrlOverride) {
        options.networkConfig = {
          ...(options.networkConfig ?? {}),
          initializeUrl: initUrlOverride,
          logEventUrl: logUrlOverride,
        };
      }
      options.networkConfig = {
        ...(options.networkConfig ?? {}),
        networkOverrideFunc: (requestUrl, requestArgs) => {
          const patchedUrl = rewriteStatsigSdkParams(requestUrl);
          if (existingNetworkOverride) {
            return existingNetworkOverride(patchedUrl, requestArgs);
          }
          return fetch(patchedUrl, requestArgs);
        },
      };
      if (overrideExp && overrideExpName) {
        const overrideAdapter = new LocalOverrideAdapter();
        options.overrideAdapter = overrideAdapter;
        overrideAdapter.overrideExperiment(
          overrideExpName,
          this._sanitizeLocalOverrideExperimentValue(JSON.parse(overrideExp))
        );
        this._locallyOverriddenExperimentNames.add(overrideExpName);
      }

      StatsigMetadataProvider.add({
        sdkType: SIDECAR_SDK_TYPE,
        sdkVersion: SIDECAR_SDK_VERSION,
      });
      this._statsigInstance = new StatsigClient(apiKey, user, options);
      await this._statsigInstance.initializeAsync();
      runStatsigAutoCapture(this._statsigInstance);

      this._clientInitialized = true;
      this._flushQueuedEvents();
      this._observePageChanges(expIds);
      this.runAllExperiments(expIds);
    } catch (e) {
      console.error("Failed to initialize Statsig:", e);
    }
    this.resetBody();
  },
});

if (document.currentScript && document.currentScript.src) {
  const url = new URL(document.currentScript.src);
  const apiKey = url.searchParams.get("apikey");
  const multiExpIds = url.searchParams.get("multiexpids");
  const initUrl = url.searchParams.get("initializeurl");
  const logUrl = url.searchParams.get("logeventurl");

  const autoStart = url.searchParams.get("autostart") !== "0";
  // Deprecated
  // const autoCapture = url.searchParams.get('autocapture') !== '0';
  const reduceFlicker = url.searchParams.get("reduceflicker") !== "0";
  StatsigSidecar.scriptNonce = document.currentScript.nonce;
  if (apiKey) {
    if (reduceFlicker) {
      document.write('<style id="__sbpd">body { display: none; }</style>\n');
      setTimeout(() => {
        StatsigSidecar.resetBody();
      }, 1000);
    }
    const expIds = multiExpIds ? multiExpIds.split(",") : null;
    StatsigSidecar.setupStatsigSdk(apiKey, expIds, autoStart, initUrl, logUrl);
    document.addEventListener(`sidecar_${apiKey}`, (e) => {
      StatsigSidecar.processEvent(e);
      e.preventDefault();
    });
  }
}
